import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../components/ui/AppShell";
import { Card, EmptyState, SectionHeading, TextLink } from "../components/ui";
import { Stagger, StaggerItem } from "../components/ui/motion";
import { Path } from "../components/icons";

import ContinueCard from "../components/dashboard/ContinueCard";
import ExploreSections from "../components/dashboard/ExploreSections";
import AchievementsPanel from "../components/dashboard/AchievementsPanel";
import AnalyticsSection from "../components/dashboard/AnalyticsSection";
import LearningPathCard from "../components/learning/LearningPathCard";
import AssignedItemCard from "../components/learning/AssignedItemCard";
import { Warm } from "./Warm";

import {
  getUserActivity,
  getUserPermissionSections,
} from "../services/UserReportService";
import { getMyAssignments } from "../services/AssignmentService";
import { openAssignedItem, pickResumeItem } from "../lib/learning";

/**
 * The learner's home.
 *
 * Ordered by what a learner actually needs, in order: what to do next, what
 * they've been assigned, what else exists, a low-stakes way to practise, and
 * only then the retrospective numbers. The previous dashboard led with a grid
 * of stock photos and buried the plan behind a button; everything here is one
 * scroll and no clicks.
 *
 * Every figure on this page is server-derived — activity from
 * `/api/user-report/activity`, the plan from `/api/assignment/me`, sections
 * from `/api/user-sections`. Nothing is synthesised client-side beyond
 * arithmetic on those responses.
 */
export const Categories = () => {
  const navigate = useNavigate();
  const warmUpRef = useRef(null);

  const [activity, setActivity] = useState({ time: null, count: 0 });
  const [sections, setSections] = useState([]);
  const [plan, setPlan] = useState([]);
  const [planLoading, setPlanLoading] = useState(true);

  const handleUnauthorized = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("savedLayouts");
    navigate("/");
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await getUserPermissionSections();
        setSections(data || []);
      } catch (e) {
        if (e?.message === "Unauthorized") handleUnauthorized();
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { count, time } = (await getUserActivity()) || {};
        setActivity({ time, count });
      } catch (e) {
        if (e?.message === "Unauthorized") handleUnauthorized();
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setPlan(await getMyAssignments());
      setPlanLoading(false);
    })();
  }, []);

  /**
   * Intranet is a destination every role can reach, but it isn't a permissioned
   * "section" the API enumerates — so it's appended rather than fetched.
   */
  const exploreSections = useMemo(() => {
    const list = [...sections];
    if (!list.some((s) => String(s.name).toLowerCase() === "intranet")) {
      list.push({ name: "Intranet", totalPercentage: 0 });
    }
    return list;
  }, [sections]);

  const resumeItem = useMemo(() => pickResumeItem(plan), [plan]);

  // The dashboard previews the plan; the plan page shows all of it. Paths lead
  // because they're the longest commitment, then the nearest standalone work.
  const previewItems = useMemo(() => {
    const paths = plan.filter((i) => i.contentType === "learning-path");
    const singles = plan.filter(
      (i) => i.contentType !== "learning-path" && i.status !== "completed"
    );
    return { paths: paths.slice(0, 2), singles: singles.slice(0, 3) };
  }, [plan]);

  const open = (item) => openAssignedItem(item, navigate);

  const selectSection = (name) => {
    localStorage.setItem("section", name);
    navigate(name === "Intranet" ? "/intranet" : "/modules");
  };

  return (
    <AppShell crumb="Home" active="dashboard">
      <ContinueCard
        item={resumeItem}
        activeTime={activity.time}
        loginCount={activity.count}
        onResume={open}
      />

      <section className="ds-section">
        <SectionHeading
          title="My Learning Plan"
          description="Assigned by your trainer — paths run in order, standalone items can be taken anytime."
          action={
            <TextLink onClick={() => navigate("/my-learning")}>View all</TextLink>
          }
        />

        {planLoading ? (
          <p className="ds-sub">Loading your plan…</p>
        ) : plan.length === 0 ? (
          <Card>
            <EmptyState
              icon={Path}
              text="Nothing has been assigned to you yet. Explore the sections below in the meantime."
            />
          </Card>
        ) : (
          <div className="ds-stack ds-gap-4">
            {previewItems.paths.map((item) => (
              <LearningPathCard key={item._id} item={item} onOpenStep={open} />
            ))}

            {previewItems.singles.length > 0 && (
              <Stagger className="ds-grid ds-grid--3">
                {previewItems.singles.map((item) => (
                  <StaggerItem key={item._id}>
                    <AssignedItemCard item={item} onOpen={open} />
                  </StaggerItem>
                ))}
              </Stagger>
            )}
          </div>
        )}
      </section>

      <ExploreSections sections={exploreSections} onSelect={selectSection} />

      <div ref={warmUpRef}>
        <Warm embedded />
      </div>

      <AchievementsPanel planItems={plan} />

      <AnalyticsSection
        onGoToWarmUp={() =>
          warmUpRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      />
    </AppShell>
  );
};

export default Categories;
